/**
 * <QuestStepRow> — une étape d'une quête, en ligne d'une carte-liste (molecule).
 *
 * Le détail d'une quête pose ses étapes les unes sous les autres : celles qui sont faites, celle
 * où l'élève en est, celles qui viennent. Une ligne dit laquelle par sa pastille (une coche verte,
 * « vert pour l'acquis » ; son numéro au corail de l'action ; son numéro éteint), puis la phrase de
 * l'étape et, tant qu'elle n'est pas faite, ce qu'elle rapporte, en glyphes pleins du jeu : la chip
 * de gain à pétale et le titre en médaille. Une étape faite ne redit pas ce qu'elle a rapporté,
 * comme aujourd'hui. Ce que rapporte une étape à venir garde ses formes sans leur couleur, sans
 * chip : sept chips de gain empilées crieraient plus fort que l'étape en cours, et le corail est à
 * l'action.
 *
 * Comme `<RankingRow>`, elle ne dessine pas de carte et ne presse pas. L'étape en cours a aussi sa
 * `<QuestCard>`, avec sa barre : ici elle n'est qu'un repère dans le chemin.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { formatGain } from "../../utils/gameAmount"
import { CardListRow } from "../CardList"
import { GainChip } from "../GainChip"
import { GAME_GLYPH_SIZE, GameGlyph } from "../GameGlyph"
import { TitleMedal } from "../TitleMedal"
import { type UIQuestStepStatus, type UIQuestStep } from "../../../ports/primary/ui/types"

export type { UIQuestStepStatus, UIQuestStep }

export type QuestStepRowProps = {
    step: UIQuestStep
    testID?: string
}

/** La pastille de l'étape : la taille du crayon de l'avatar, lisible sans peser. */
export const QUEST_STEP_MARKER_SIZE = 28

const STATE_WORDS: Record<UIQuestStepStatus, string> = {
    done: "terminée",
    current: "en cours",
    locked: "pas encore débloquée",
}

export function QuestStepRow(props: QuestStepRowProps) {
    const { step, testID } = props
    const { step: number, description, status, petals, titleGained } = step

    const done = status === "done"
    const locked = status === "locked"
    const hasPetals = !done && petals != null && petals > 0
    const hasTitle = !done && titleGained != null && titleGained !== ""

    return (
        <View
            testID={testID}
            accessible
            accessibilityLabel={[
                `Étape ${number}, ${STATE_WORDS[status]}`,
                description,
                hasPetals ? formatGain(petals, "petals") : null,
                hasTitle ? `Titre : ${titleGained}` : null,
            ]
                .filter(Boolean)
                .join(", ")}
        >
            <CardListRow>
                <Row gap="3" align="start">
                    <View style={styles.marker(status)}>
                        {done ? (
                            <Icon name="check" size={16} color="white" />
                        ) : (
                            <Text size="text-sm" weight="bold" color={locked ? "text-muted" : "text-on-role"}>
                                {number}
                            </Text>
                        )}
                    </View>
                    <Stack gap="2" style={styles.body}>
                        <Text
                            size="text-md"
                            weight={status === "current" ? "bold" : "regular"}
                            color={locked ? "text-muted" : "text-primary"}
                            style={styles.phrase}
                        >
                            {description}
                        </Text>
                        {(hasPetals || hasTitle) && !locked && (
                            <Row gap="2" style={styles.chips}>
                                {hasPetals && <GainChip amount={petals} unit="petals" />}
                                {hasTitle && <TitleMedal title={titleGained} />}
                            </Row>
                        )}
                        {(hasPetals || hasTitle) && locked && (
                            <Row gap="3" style={styles.chips}>
                                {hasPetals && (
                                    <Row gap="1">
                                        <GameGlyph
                                            name="petals"
                                            size={GAME_GLYPH_SIZE.chip}
                                            tone="muted"
                                            plate={false}
                                        />
                                        <Text size="text-sm" weight="bold" color="text-muted">
                                            {formatGain(petals, "petals")}
                                        </Text>
                                    </Row>
                                )}
                                {hasTitle && <TitleMedal title={titleGained} tone="muted" />}
                            </Row>
                        )}
                    </Stack>
                </Row>
            </CardListRow>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    marker: (status: UIQuestStepStatus) => ({
        width: QUEST_STEP_MARKER_SIZE,
        height: QUEST_STEP_MARKER_SIZE,
        borderRadius: QUEST_STEP_MARKER_SIZE / 2,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        flexShrink: 0,
        backgroundColor:
            status === "done"
                ? theme.colors.success
                : status === "current"
                  ? theme.colors["role-fill"]
                  : theme.colors["surface-subtle"],
    }),
    body: { flex: 1, flexShrink: 1, minWidth: 0 },
    /** La phrase se cale sur le centre de la pastille quand elle tient sur une ligne. */
    phrase: { paddingTop: theme.spacing["1"] },
    chips: { flexWrap: "wrap" },
}))
